import React from 'react';
import { Plus, Check } from 'lucide-react';
import { getTextPreview } from '../../../../utils/sanitization';
import type { TemplateSelectionStepProps } from '../types';

/**
 * Template selection step for campaign creation.
 * Displays templates with preview and allows creating new ones.
 */
export const TemplateSelectionStep: React.FC<TemplateSelectionStepProps> = ({
  templates,
  hoveredTemplateId,
  onHoverTemplate,
  onSelectTemplate,
  onPreviewTemplate,
  onCreateTemplate,
}) => {
  const selectedTemplate = templates.find((t) => t.id === hoveredTemplateId);

  return (
    <div className="space-y-2 mt-3">
      {templates.length === 0 ? (
        <div className="text-center py-4 border border-dashed border-gray-200 rounded-lg">
          <p className="text-[12px] text-gray-400">No templates yet</p>
        </div>
      ) : (
        templates.slice(0, 5).map((template) => {
          const firstLine = getTextPreview(template.body);
          const isSelected = hoveredTemplateId === template.id;

          return (
            <div
              key={template.id}
              onClick={() => onHoverTemplate(isSelected ? null : template.id)}
              className={`px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${
                isSelected
                  ? 'border-gray-800 bg-gray-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-start gap-2.5">
                <div
                  className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'border-gray-800 bg-gray-800' : 'border-gray-300'
                  }`}
                >
                  {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-gray-800 truncate">
                    {template.subject}
                  </p>
                  <p className="text-[11px] text-gray-400 truncate mt-0.5">
                    {firstLine}
                    {firstLine.length >= 60 ? '...' : ''}
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreviewTemplate(template);
                    }}
                    className="text-[11px] text-gray-500 hover:text-gray-700 hover:underline mt-1.5 transition-colors"
                  >
                    See full template
                  </button>
                </div>
              </div>
            </div>
          );
        })
      )}

      <button
        onClick={onCreateTemplate}
        className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
      >
        <Plus className="w-4 h-4 text-gray-400" />
        <span className="text-[13px] text-gray-500">Create new template</span>
      </button>

      {selectedTemplate && (
        <div className="mt-4">
          <button
            onClick={() => onSelectTemplate(selectedTemplate)}
            className="px-4 py-2 text-[13px] text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Use this template
          </button>
        </div>
      )}
    </div>
  );
};

TemplateSelectionStep.displayName = 'TemplateSelectionStep';
